<template>
  <app-spinner v-if="loading" class="mt-4" />

  <div v-else>
    <ClaimInfoModal v-if="showInfoModal" :brand="offer.brand" @claim-info-modal-close="showInfoModal = false" />

    <WaitListModal
      v-if="showWaitListModal"
      :brand="offer.brand"
      :offer-id="offer.id"
      :address-id="currentAddress.address_id"
      @wait-list-modal-close="showWaitListModal = false"
    />

    <ConfirmLocationModal
      v-if="isLocalOffer && currentAddress"
      :modal-visible="showConfirmLocationModal"
      :addresses="addresses"
      :current-address="currentAddress"
      :confirm-mode="confirmLocationConfirmMode"
      :confirm-location-loading="activateVoucherLoading"
      @confirm-location-modal-close="showConfirmLocationModal = false"
      @confirm-location-modal-location-selected="handleConfirmLocation"
    />

    <ClaimOfferCityListModal
      v-if="showClaimOfferCityListModal"
      :addresses="addresses"
      @claim-offer-city-list-modal-city-selected="handleCitySelected"
      @claim-offer-city-list-modal-close="showClaimOfferCityListModal = false"
    />

    <v-container class="pa-0">
      <v-row align="center" justify="center" class="ma-0">
        <v-col cols="12" class="pa-0">
          <v-img :src="offer.offer_image_url" contain width="100%" max-height="300" />
        </v-col>
      </v-row>
    </v-container>

    <v-container>
      <v-row align="center" justify="center" class="mt-4 mb-2">
        <v-col cols="11" class="py-0">
          <span class="text-left header">Everything You Need to Know About this Offer</span>
        </v-col>
      </v-row>

      <v-row align="center" justify="center" class="section-divider">
        <v-col cols="11" align="center" justify="center">
          <v-divider />
        </v-col>
      </v-row>

      <v-row v-if="isUrlOrderFlowOffer" align="center" justify="center" class="mb-2">
        <v-col cols="11" class="pa-0 pl-3">
          <v-avatar color="primary" size="34" class="mt-n3 mr-3">
            <span class="white--text font-weight-bold">1</span>
          </v-avatar>
          <span class="text-left section-header">Explore the Website</span>

          <v-row class="mt-2">
            <v-col cols="1" class="pa-0 pl-5">
              <v-icon small color="black" class="mt-n1">mdi-web</v-icon>
            </v-col>
            <v-col cols="11" class="pa-0 pl-2">
              <a :href="offerUrl" class="ml-2 font-weight-bold" data-cy="offerDescriptionUrl">{{ offerUrl }}</a>
            </v-col>
          </v-row>

          <v-row v-if="currentAddress" class="mt-4" align="center" justify="center">
            <v-col class="pa-0 pl-5" cols="1" align="center" justify="center">
              <v-icon small color="black" class="mt-n1">mdi-map-marker-outline</v-icon>
            </v-col>
            <v-col class="pt-0 pb-0" :cols="numLocations > 1 ? 8 : 11" justify="center">
              <span data-cy="locationToVisit">{{ currentAddress.address }} {{ currentAddress.city }}</span>
            </v-col>
            <v-col v-if="numLocations > 1" class="pt-0 pb-0" cols="3">
              <span class="primary--text font-weight-medium text-decoration-underline" @click="onChangeLocationClicked">Change</span>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <v-row v-else align="center" justify="center" class="mb-2">
        <v-col cols="11" class="pa-0 pl-3">
          <v-avatar color="primary" size="34" class="mt-n3 mr-3">
            <span class="white--text font-weight-bold">1</span>
          </v-avatar>
          <span v-if="isEventOffer" class="text-left section-header">Location and Time</span>
          <span v-else class="text-left section-header">Location to Visit</span>
          <v-row v-if="isLocalOffer && !currentAddress" class="mt-4 mb-1">
            <v-col class="pa-0 pl-3" cols="11" align="center" justify="center">
              <v-btn color="primary" block rounded @click="showClaimOfferCityListModal = true">Choose a city</v-btn>
            </v-col>
          </v-row>
          <div v-else>
            <v-row v-if="isWaitList" class="mt-2 mb-n2" align="center" justify="center">
              <v-col class="pt-0 pb-0 mr-n2" cols="1" align="center" justify="center">
                <v-icon small color="primary" class="mt-n1">mdi-alert-outline</v-icon>
              </v-col>
              <v-col class="pt-0 pb-0" cols="11">
                <span class="font-weight-light primary--text">The selected location is out of offers</span>
              </v-col>
            </v-row>
            <v-row v-if="currentAddress" class="pt-0 pb-0 mt-2" align="center" justify="center">
              <v-col class="pt-0 pb-0 mr-n2" cols="1" align="center" justify="center">
                <v-icon small color="black" class="mt-n1">mdi-map-marker-outline</v-icon>
              </v-col>
              <v-col class="pt-0 pb-0" :cols="numLocations > 1 ? 8 : 11" justify="center">
                <span data-cy="locationToVisit">{{ currentAddress.address }} {{ currentAddress.city }}</span>
              </v-col>
              <v-col v-if="numLocations > 1" class="pt-0 pb-0" cols="3">
                <span class="primary--text font-weight-medium text-decoration-underline" @click="onChangeLocationClicked">Change</span>
              </v-col>
            </v-row>
            <v-row v-if="currentAddress" class="pt-3 pb-3 mt-2" align="center" justify="center">
              <v-col class="pt-0 pb-0 mr-n2" cols="1" align="center" justify="center">
                <v-icon small color="black" class="mt-n1">mdi-car-outline</v-icon>
              </v-col>
              <v-col class="pt-0 pb-0" cols="11">
                <span class="font-weight-light">
                  {{ currentAddress.distance | roundedFull }} mi <span class="px-2">•</span> {{ numLocations }} participating
                  {{ 'location' | pluralize(numLocations) }}
                </span>
              </v-col>
            </v-row>
            <v-row v-if="isCallOrderFlowOffer" class="pt-3 pb-3 mt-n3" align="center" justify="center">
              <v-col class="pt-0 pb-0 mr-n2" cols="1" align="center" justify="center">
                <v-icon small color="black" class="mt-n1">mdi-phone</v-icon>
              </v-col>
              <v-col class="pt-0 pb-0" cols="11">
                <span class="font-weight-light"> This offer will require you to schedule an appointment to redeem </span>
              </v-col>
            </v-row>
            <v-row v-if="isEventOffer" class="mt-2" justify="center">
              <v-col class="pt-0 pb-0 mr-n2" cols="1" justify="center">
                <v-icon small color="black" class="mt-n1">mdi-calendar</v-icon>
              </v-col>
              <v-col class="pt-0 pb-0 mt-0" cols="11">
                <div style="display: inline-block" :class="selectEventTimeClasses">
                  <span ref="EventTimeSelection" class="font-weight-medium">Select a time to attend:</span>
                </div>
                <v-radio-group v-model="eventDateSelection" class="font-weight-medium pa-0">
                  <v-radio
                    v-for="(date, index) in displayEventTimes"
                    :key="date.id"
                    :value="date.id"
                    :disabled="!date.vouchers_left || date.vouchers_left <= 0"
                  >
                    <template #label>
                      <v-row :data-cy="`eventDate-${index}`">
                        <v-col cols="8" class="mr-4 pa-0" align="right">
                          <strong>{{ dateToDayWeek(date.event_date) }}</strong>
                          {{ date.start_time }} - {{ date.end_time }}
                        </v-col>
                        <v-col cols="3" class="ma-0 pa-0">
                          <span v-if="date.vouchers_left">
                            <strong class="primary--text">{{ date.vouchers_left }}</strong>
                            <v-icon dense small color="primary" class="pl-1 mt-n1">mdi-ticket-confirmation-outline</v-icon>
                          </span>
                          <span v-else>
                            <strong>Sold Out</strong>
                          </span>
                        </v-col>
                      </v-row>
                    </template>
                  </v-radio>
                </v-radio-group>
              </v-col>
            </v-row>

            <v-row v-if="isWaitList && vouchersLeftNearby > 0" class="pt-0 pb-0" align="center" justify="center">
              <v-col cols="11">
                <v-row>
                  <v-col cols="12" class="pl-1">
                    <v-btn color="primary" block rounded @click="onChangeLocationClicked">Change Location</v-btn>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" class="pl-1">
                    <v-btn color="primary" outlined block rounded @click="onJoinWaitListClicked">Join WaitList</v-btn>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" class="pt-0 pb-0 mr-n2">
                    <span class="font-weight-light font-italic caption">
                      There's {{ vouchersLeftNearby }} {{ 'location' | pluralize(vouchersLeftNearby) }} nearby with available offers
                    </span>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </div>
        </v-col>
      </v-row>

      <v-row align="center" justify="center" class="section-divider">
        <v-col cols="11" align="center" justify="center">
          <v-divider />
        </v-col>
      </v-row>

      <v-row align="center" justify="center">
        <v-col cols="11" class="pa-0 pl-3">
          <v-avatar color="primary" size="34" class="mt-n3 mr-3">
            <span class="white--text font-weight-bold">2</span>
          </v-avatar>
          <span class="text-left section-header">What You Get</span>
        </v-col>
      </v-row>
      <v-row justify="center" align="center" class="ma-0 mt-2 w100 pa-0">
        <v-col cols="11">
          <div align="center" justify="center" class="ticket">
            <div class="ticket__shape">
              <div class="top"></div>
              <div class="rip"></div>
              <div class="bottom"></div>
            </div>
            <div class="ticket__content break-word">
              <div class="text--secondary pl-5 pr-5" data-cy="offerSummary">{{ offer.offer_summary }}</div>
              <div class="caption mt-4">NOT A VALID COUPON MUST CLAIM FIRST</div>
            </div>
          </div>
        </v-col>
      </v-row>

      <v-row align="center" justify="center" class="section-divider">
        <v-col cols="11" align="center" justify="center">
          <v-divider />
        </v-col>
      </v-row>

      <v-row align="center" justify="center">
        <v-col cols="11">
          <v-avatar color="primary" size="34" class="mt-n3 mr-3">
            <span class="white--text font-weight-bold">3</span>
          </v-avatar>
          <span class="text-left section-header">What You Do In Return</span>
          <h4 class="text-bold pt-1 mb-2">Do {{ offer.number_tasks_required }} of the following actions after you use your offer:</h4>
        </v-col>
      </v-row>
      <v-row align="center" justify="center">
        <v-col cols="11">
          <OfferTaskList :task-types="offer.tasks" />
        </v-col>
      </v-row>

      <v-row align="center" justify="center" class="section-divider">
        <v-col cols="11" align="center" justify="center">
          <v-divider />
        </v-col>
      </v-row>

      <v-row align="center" justify="center">
        <v-col cols="11" class="pa-0 pl-3">
          <span class="text-left section-header">How it works</span>
          <v-icon dense color="black" class="ml-1 mt-n3" @click="showInfoModal = true">mdi-information-outline</v-icon>
        </v-col>
      </v-row>
      <v-row justify="center">
        <v-col cols="10" class="pa-0 pt-2">
          <ul class="linked-bullets">
            <li class="font-weight-light">Tap "{{ callToActionText }}" to claim your voucher.</li>
            <li v-if="isEventOffer" class="font-weight-light">Use your voucher on the event date you selected.</li>
            <li v-else class="font-weight-light">Use your voucher before the expiration.</li>
            <li class="font-weight-light">Complete {{ offer.number_tasks_required }} of the available actions above.</li>
          </ul>
        </v-col>
      </v-row>

      <div v-if="offer.offer_special_instructions">
        <v-row align="center" justify="center" class="section-divider">
          <v-col cols="11" align="center" justify="center">
            <v-divider />
          </v-col>
        </v-row>

        <v-row align="center" justify="center">
          <v-col cols="11" class="pa-0 pl-3">
            <span class="text-left section-header">Special Instructions</span>
          </v-col>
        </v-row>
        <v-row justify="center" class="mb-5">
          <v-col cols="10" class="pa-0 pt-2 font-weight-light">
            {{ offer.offer_special_instructions }}
          </v-col>
        </v-row>
      </div>

      <v-row align="center" justify="center" class="section-divider">
        <v-col cols="11" align="center" justify="center">
          <v-divider />
        </v-col>
      </v-row>

      <v-row align="center" justify="center">
        <v-col cols="11" class="pa-0 pl-3">
          <span class="text-left section-header">Limitations</span>
        </v-col>
      </v-row>
      <v-row justify="center" class="mb-5">
        <v-col cols="10" class="pa-0 pt-2 pb-4">
          <ul>
            <li v-if="offer.min_days_between_offer && offer.min_days_between_offer !== 36500" class="font-weight-light">
              Can only be used once every {{ offer.min_days_between_offer }} days.
            </li>
            <li v-if="offer.min_days_between_offer && offer.min_days_between_offer === 36500" class="font-weight-light">Can only be used once.</li>
            <li class="font-weight-light pt-2">Limit 1 per group.</li>
            <li class="font-weight-light pt-2">Not valid with any other coupon or offer.</li>
          </ul>
        </v-col>
      </v-row>

      <v-footer app :fixed="true" :elevation="6" class="white">
        <v-row class="py-1">
          <v-col cols="6" class="pr-0 body-2">
            <div v-if="!isEventOffer">
              <v-icon dense small class="pr-1 mt-n1">mdi-clock-outline</v-icon>
              Expires in {{ offer.expiration_date | timeUntil }}
            </div>
            <div v-else>
              <v-icon dense small class="pr-1 mt-n1">mdi-clock-outline</v-icon>
              {{ displaySelectedEventTime || 'Select a date to attend.' }}
            </div>
            <div class="primary--text pt-1">
              <v-icon dense small color="primary" class="pr-1 mt-n1">mdi-ticket-confirmation-outline</v-icon>
              {{ vouchersLeft | manyNumberPretty }} Available
            </div>
          </v-col>
          <v-col cols="6" class="pl-0 pt-4 text-center">
            <v-btn v-if="isLocalOffer && !currentAddress" color="primary" block rounded @click="showClaimOfferCityListModal = true">Choose a city</v-btn>
            <v-btn v-else-if="isWebOffer" :loading="activateVoucherLoading" color="primary" block rounded @click="claimOffer()">{{ callToActionText }}</v-btn>
            <v-btn v-else-if="isWaitList" :loading="activateVoucherLoading" color="primary" block rounded @click="onJoinWaitListClicked()">
              Join Waitlist
            </v-btn>
            <v-btn
              v-else-if="isEventOffer && !eventDateSelection"
              :loading="activateVoucherLoading"
              color="primary"
              block
              rounded
              @click="slideToEventTimeSelection()"
            >
              Select A Time
            </v-btn>

            <v-btn v-else :loading="activateVoucherLoading" color="primary" block rounded @click="onConfirmLocationClicked()">{{ callToActionText }}</v-btn>
          </v-col>
        </v-row>
      </v-footer>
    </v-container>
  </div>
</template>

<script>
import Vue from 'vue'
import { mapActions, mapGetters } from 'vuex'

import OfferTaskList from '@/components/OfferTaskList.vue'
import WaitListModal from '@/components/waitList/WaitListModal.vue'
import ConfirmLocationModal from '@/components/ConfirmLocationModal.vue'
import ClaimOfferCityListModal from '@/components/ClaimOfferCityListModal.vue'
import ClaimInfoModal from '@/components/ClaimInfoModal.vue'

import { ApiService } from '@/services'
import { ReservationCategory } from '@/enums/reservationCategory'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

export default Vue.extend({
  name: 'ClaimOffer',

  metaInfo() {
    return {
      title: `${this.offer ? this.offer.brand : ''} | Claim Offer`,
    }
  },

  components: {
    OfferTaskList,
    WaitListModal,
    ConfirmLocationModal,
    ClaimInfoModal,
    ClaimOfferCityListModal,
  },

  props: {
    offerId: {
      type: String,
      required: true,
    },
    addressId: {
      type: String,
      required: false,
      default: null,
    },
    fromLong: {
      type: String,
      required: false,
      default: null,
    },
    fromLat: {
      type: String,
      required: false,
      default: null,
    },
  },

  data: () => ({
    loading: true,
    activateVoucherLoading: false,
    offer: null,
    addresses: [],
    currentAddress: null,
    showWaitListModal: false,
    confirmLocationConfirmMode: false,
    showConfirmLocationModal: false,
    showClaimOfferCityListModal: false,
    showInfoModal: false,
    eventDateSelection: null,
    possibleEventDates: [],
    selectEventTimeClasses: [''],
  }),

  computed: {
    ...mapGetters('auth', ['loggedIn']),
    isLocalOffer: function () {
      return this.offer.offer_type === 'LOCAL'
    },
    isWebOffer: function () {
      return this.offer.offer_type === 'WEB'
    },
    isEventOffer: function () {
      return this.offer.offer_order_flow_type === 'EVENT'
    },
    isUrlOrderFlowOffer: function () {
      return this.offer.offer_order_flow_type === 'URL'
    },
    isCallOrderFlowOffer: function () {
      return this.offer.offer_order_flow_type === 'CALL'
    },
    displaySelectedEventTime() {
      if (!this.possibleEventDates || !this.eventDateSelection) return null
      const retD = this.possibleEventDates.find((e) => e.event_choice.id === this.eventDateSelection).event_choice.event_date
      return this.dateToDayWeek(retD)
    },
    displayEventTimes() {
      if (this.possibleEventDates) {
        return this.possibleEventDates.map((e) => {
          return { ...e.event_choice, vouchers_left: e.vouchers_left }
        })
      }
      return this.offer.event_date_option
    },
    isWaitList: function () {
      return !this.isEventOffer && this.vouchersLeft <= 0
    },
    callToActionText: function () {
      if (this.isWebOffer) return 'I Want This'

      return 'Claim this Offer'
    },
    vouchersLeft: function () {
      return this.currentAddress ? this.currentAddress.vouchers_left : this.offer.vouchers_left
    },
    vouchersLeftNearby: function () {
      return this.addresses.filter((address) => address.vouchers_left > 0 && address.distance <= 5).length
    },
    numLocations: function () {
      return this.addresses.length
    },
    offerUrl: function () {
      return this.offer.offer_description_url || this.offer.offer_url
    },
  },

  watch: {
    eventDateSelection: function () {
      this.selectEventTimeClasses = []
    },
  },
  mounted: function () {
    this.fetchOffer(this.fromLat, this.fromLong)
  },

  methods: {
    ...mapActions('auth', ['showLoginModal']),
    slideToEventTimeSelection() {
      var element = this.$refs['EventTimeSelection']
      var top = element.offsetTop
      window.scrollTo({ left: 0, top, behavior: 'smooth' })
      this.selectEventTimeClasses.push('wiggle')
    },
    dateToDayWeek(s) {
      return dayjs(s).format('ddd MMM D')
    },
    fetchOffer(fromLat, fromLong) {
      this.loading = true

      ApiService.get(`claim/${this.offerId}`, { params: { fromLat, fromLong } }).then((response) => {
        if (response.data.error || response.data.offer.offer_status !== 'ACTIVE') {
          this.$router.replace({ path: '/' })
          this.$toast.warning('Whoops! That offer is no longer available.')
          return
        }

        if (response.data.offer.voucher) {
          this.navigateToVoucher(response.data.offer.voucher.id)
          return
        }

        this.offer = response.data.offer
        this.addresses = response.data.addresses
        this.currentAddress = this.addresses.find((address) => address.address_id === this.addressId)

        if (!this.addressId && this.addresses.length === 1) {
          const params = { ...this.$route.params, addressId: this.addresses[0].address_id }
          this.$router.replace({ name: this.$route.name, params })

          this.currentAddress = this.addresses[0]
        }

        if (this.offer.offer_order_flow_type === 'EVENT') {
          ApiService.get(`claim/${this.offerId}/eventDataByAddress/${this.currentAddress.address_id}`).then((r) => {
            this.possibleEventDates = r.data
          })
        }

        this.loading = false
      })
    },
    claimOffer(addressId) {
      if (!this.loggedIn) {
        this.showLoginModal()
        return
      }

      this.activateVoucherLoading = true

      const voucherReserveUrl = `voucherWorkflow/reserve/${this.offer.offer_id}/${addressId ? addressId : ''}`

      ApiService.post(voucherReserveUrl, { selectedEventDateId: this.eventDateSelection })
        .then((response) => {
          if (response.data.error) {
            throw Error(response.data.error)
          }

          this.$store.dispatch('reservations/fetchReservationSummary')
          dayjs.extend(relativeTime)
          this.$toast.success(`This offer is now yours! You have ${dayjs().to(this.offer.expiration_date, true)} days to use it`)
          this.$router.replace({ path: `/user/offers/${ReservationCategory.CLAIMED}` })
        })
        .catch((error) => {
          this.activateVoucherLoading = false
          this.showConfirmLocationModal = false
          console.log(`${error.message} ${error.message === 'Whoops! Redeemed too recently.'}`)
          if (error.message == 'Whoops! Redeemed too recently.') {
            if (this.offer.min_days_between_offer === 36500) {
              this.$toast.error(`Whoops! This offer can only be redeemed once.`)
            } else {
              this.$toast.error(`Whoops! This offer can only be redeemed every "${this.offer.min_days_between_offer}" days.`)
            }
          } else {
            this.$toast.error(error.message, { timeout: 5000 })
          }
        })
    },
    handleConfirmLocation(addressId) {
      if (this.addressId !== addressId) {
        const params = { ...this.$route.params, addressId }
        this.$router.replace({ name: this.$route.name, params })
      }

      const address = this.addresses.find((address) => address.address_id === addressId)
      this.currentAddress = address

      if (address.vouchers_left <= 0 || !this.confirmLocationConfirmMode) {
        this.showConfirmLocationModal = false
        this.$toast.success('Location updated!')
        return
      }

      this.claimOffer(addressId)
    },
    handleCitySelected(city) {
      const params = { ...this.$route.params, addressId: city.addressId, fromLat: city.latitude, fromLong: city.longitude }
      this.$router.replace({ name: this.$route.name, params })

      this.fetchOffer(city.latitude, city.longitude)

      this.showClaimOfferCityListModal = false
    },
    onChangeLocationClicked() {
      this.confirmLocationConfirmMode = false
      this.showConfirmLocationModal = true
    },
    onConfirmLocationClicked() {
      if (!this.loggedIn) {
        this.showLoginModal()
        return
      }

      this.confirmLocationConfirmMode = true
      this.showConfirmLocationModal = true
    },
    onJoinWaitListClicked() {
      if (!this.loggedIn) {
        this.showLoginModal()
        return
      }

      this.showWaitListModal = true
    },
  },
})
</script>

<style lang="scss" scoped>
.header {
  font-size: 28px;
  font-weight: 700;
  line-height: 1.1em;
}
.w100 {
  width: 100%;
  overflow: hidden;
}

.section-header {
  font-size: 24px;
  font-weight: 500;
}

.section-divider {
  margin-top: 18px !important;
  margin-bottom: 18px !important;
}

ul.linked-bullets li {
  position: relative;
  margin-bottom: 0;
  padding-bottom: 1em;
}

ul.linked-bullets {
  list-style: none;
}

ul.linked-bullets li:after {
  /* bullets */
  position: absolute;
  left: -26px;
  top: 3px;
  content: '';
  color: black;
  background-color: white;
  border-radius: 50%;
  border: solid 1px black;
  min-width: 16px;
  min-height: 16px;
}

ul.linked-bullets li:before {
  /* lines */
  content: '';
  position: absolute;
  left: -18px;
  border-left: 1px solid black;
  height: 100%;
  width: 1px;
}

ul.linked-bullets li:first-child:before {
  /* first li's line */
  top: 6px;
}

ul.linked-bullets li:last-child:before {
  /* last li's line */
  height: 6px;
}

.ticket {
  position: relative;

  &__shape {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    position: absolute;
    z-index: 0;
    filter: drop-shadow(2px 5px 5px rgba(0, 0, 0, 0.15));

    .top,
    .bottom {
      flex: 1;
      width: 100%;
      background-color: #fff;
    }

    .top {
      border-radius: 5px 5px 0 0;
    }

    .bottom {
      border-radius: 0 0 5px 5px;
    }

    .ticket__content {
      word-break: break-word;
    }

    .rip {
      position: relative;
      height: 40px;
      flex-shrink: 0;
      margin: 0px 20px;
      background-color: #fff;
      width: calc(100% - 40px);

      &::before,
      &::after {
        position: absolute;
        content: '';
        width: 40px;
        height: 40px;
        border: 10px solid transparent;
        -webkit-box-sizing: content-box;
        box-sizing: content-box;
        border-top-color: #fff;
        border-right-color: #fff;
        transform: translate(-50%, -50%) rotate(45deg);
        border-radius: 50%;
        top: 50%;
      }

      &::before {
        left: -20px;
      }

      &::after {
        right: -80px;
        transform: translate(-50%, -50%) rotate(225deg);
      }
    }
  }

  &__content {
    position: relative;
    z-index: 1;
    padding: 1rem;
  }
}

//
.wiggle {
  animation: wiggle 2s infinite;
  -webkit-animation: wiggle 2s infinite;
  -moz-animation: wiggle 2 infinite;
}

@keyframes wiggle {
  0% {
    transform: rotate(0deg);
  }
  80% {
    transform: rotate(0deg);
  }
  85% {
    transform: rotate(5deg);
  }
  95% {
    transform: rotate(-5deg);
  }
  100% {
    transform: rotate(0deg);
  }
}

@-webkit-keyframes wiggle {
  0% {
    transform: rotate(0deg);
  }
  80% {
    transform: rotate(0deg);
  }
  85% {
    transform: rotate(5deg);
  }
  95% {
    transform: rotate(-5deg);
  }
  100% {
    transform: rotate(0deg);
  }
}

@-moz-keyframes wiggle {
  0% {
    transform: rotate(0deg);
  }
  80% {
    transform: rotate(0deg);
  }
  85% {
    transform: rotate(5deg);
  }
  95% {
    transform: rotate(-5deg);
  }
  100% {
    transform: rotate(0deg);
  }
}
</style>
